import { CircleDashed } from "@phosphor-icons/react";
import { Fragment, useState } from "react";
import { phosphorByName } from "../icons/phosphor-by-name";
import { cn } from "../lib/cn";
import { CARD_BODY, CARD_EYEBROW } from "../primitives/type";

/** What Heartbeat decided, newest first, on the rail the design system draws for a stream of events: */

export type HeartbeatVerdict = "sent" | "held" | "quiet";

export interface HeartbeatTimelineEntry {
  id: string;
  whenLabel: string;
  dayLabel?: string;
  verdict: HeartbeatVerdict;
  headline: string;
  detail: string | null;
  signals: Array<{ label: string; icon: string }>;
  /** The lenses that fed this decision. */
  considered?: string[];
  /** How many identical decisions in a row this stands for. */
  count?: number;
}

const VERDICT_LABEL: Record<HeartbeatVerdict, string> = {
  sent: "Sent",
  held: "Held",
  quiet: "Stayed quiet",
};

export interface HeartbeatTimelineProps {
  entries: ReadonlyArray<HeartbeatTimelineEntry>;
  /** Matches the card it sits in, which matches the card above that. */
  dark?: boolean;
  className?: string;
}

export function HeartbeatTimeline({ entries, dark = false, className }: HeartbeatTimelineProps) {
  const [earlierOpen, setEarlierOpen] = useState(false);
  if (entries.length === 0) {
    return (
      <p className={cn("m-0 text-sm", dark ? "text-paper/60" : "text-gray-600", className)}>
        Nothing decided yet. This fills in as Heartbeat reads your days.
      </p>
    );
  }
  const today = entries.filter((entry) => !entry.dayLabel || entry.dayLabel === "Today");
  const earlier = entries.filter((entry) => entry.dayLabel && entry.dayLabel !== "Today");
  // Yesterday stays out of the list until today has something to stand next to.
  const canShowEarlier = today.length > 0 && earlier.length > 0;
  const visible = earlierOpen && canShowEarlier ? entries : today;
  const earlierIsYesterday = earlier.every((entry) => entry.dayLabel === "Yesterday");
  const earlierLabel = earlierOpen
    ? earlierIsYesterday
      ? "Hide yesterday"
      : "Hide earlier"
    : earlierIsYesterday
      ? "Show yesterday"
      : "Show earlier";
  if (visible.length === 0) {
    return (
      <p className={cn("m-0 text-sm", dark ? "text-paper/60" : "text-gray-600", className)}>
        Nothing decided yet. This fills in as Heartbeat reads your days.
      </p>
    );
  }
  return (
    <div className={className}>
      <ol className="relative m-0 list-none p-0">
        {/* The rail runs behind the nodes, inset to their centre line. */}
        <span
          aria-hidden="true"
          className={cn(
            "absolute top-3 bottom-3 left-[15px] w-px",
            dark ? "bg-paper/15" : "bg-mist",
          )}
        />
        {visible.map((entry, index) => {
          const sent = entry.verdict === "sent";
          const count = entry.count ?? 1;
          const considered = entry.considered ?? [];
          // A heading wherever the day changes, so a clock time is never read
          // against the wrong day.
          const newDay = entry.dayLabel && entry.dayLabel !== visible[index - 1]?.dayLabel;
          const Icon = phosphorByName(entry.signals[0]?.icon ?? "") ?? CircleDashed;
          return (
            <Fragment key={entry.id}>
              {newDay ? (
                <li className="relative flex py-2 first:pt-0">
                  <span
                    className={cn(
                      CARD_EYEBROW,
                      "relative z-10 rounded-full border px-2.5 py-1",
                      dark
                        ? "border-paper/15 bg-[var(--hb-timeline-ground,var(--color-navy))] text-paper/50"
                        : "border-leaf/40 bg-leaf-soft text-leaf-dark",
                    )}
                  >
                    {entry.dayLabel}
                  </span>
                </li>
              ) : null}
              <li className="relative flex gap-3 py-2">
                <span
                  className={cn(
                    "relative z-10 grid h-[30px] w-[30px] flex-none place-items-center rounded-full border",
                    sent
                      ? dark
                        ? "border-teal-300/45 bg-teal-300/15 text-teal-100"
                        : "border-teal-200 bg-teal-50 text-teal-700 shadow-[0_1px_2px_color-mix(in_srgb,var(--color-navy)_6%,transparent)]"
                      : dark
                        ? "border-paper/20 bg-[color-mix(in_srgb,var(--hb-timeline-ground,var(--color-navy))_60%,transparent)] text-paper/55"
                        : "border-mist-dark bg-paper text-gray-500 shadow-[0_1px_2px_color-mix(in_srgb,var(--color-navy)_6%,transparent)]",
                  )}
                >
                  <Icon size={14} weight="regular" aria-hidden="true" />
                </span>
                <div className="min-w-0 flex-1 pt-0.5 pb-1">
                  <div className="mb-1 flex items-baseline gap-2">
                    <span
                      className={cn(
                        CARD_EYEBROW,
                        sent
                          ? dark
                            ? "text-teal-100"
                            : "text-teal-700"
                          : dark
                            ? "text-paper/45"
                            : "text-gray-500",
                      )}
                    >
                      {VERDICT_LABEL[entry.verdict]}
                    </span>
                    {count > 1 ? (
                      <span
                        className={cn(
                          CARD_EYEBROW,
                          "rounded-full px-1.5 tabular-nums",
                          dark ? "bg-paper/10 text-paper/55" : "bg-mist text-gray-600",
                        )}
                      >
                        {count}×
                      </span>
                    ) : null}
                    <span
                      className={cn(
                        CARD_EYEBROW,
                        "ml-auto whitespace-nowrap tabular-nums",
                        dark ? "text-paper/40" : "text-gray-400",
                      )}
                    >
                      {entry.whenLabel}
                    </span>
                  </div>
                  {/* The conclusion carries the weight; the reason sits under it. */}
                  <p
                    className={cn(CARD_BODY, "m-0 text-pretty", dark ? "text-paper" : "text-navy")}
                  >
                    {entry.headline}
                  </p>
                  {entry.detail ? (
                    <p
                      className={cn(
                        CARD_BODY,
                        "m-0 mt-0.5 text-pretty",
                        dark ? "text-paper/60" : "text-gray-600",
                      )}
                    >
                      {entry.detail}
                    </p>
                  ) : null}
                  {considered.length > 0 ? (
                    <p
                      className={cn(
                        CARD_BODY,
                        "m-0 mt-1.5 text-pretty",
                        dark ? "text-paper/60" : "text-gray-600",
                      )}
                    >
                      {/* What it weighed, not what it concluded. A quiet row cites
                      no signals at all, so without this the commonest row on
                      the list never says what went into it. */}
                      <span className="font-bold">Weighed</span>{" "}
                      {considered.slice(0, 4).join(" · ")}
                      {considered.length > 4 ? ` +${considered.length - 4}` : ""}
                    </p>
                  ) : null}
                </div>
              </li>
            </Fragment>
          );
        })}
      </ol>
      {canShowEarlier ? (
        <button
          type="button"
          onClick={() => setEarlierOpen((open) => !open)}
          className={cn(
            CARD_EYEBROW,
            "mt-2 inline-flex transition-colors duration-200",
            dark ? "text-paper/75 hover:text-paper" : "text-teal-700 hover:text-teal-800",
          )}
        >
          {earlierLabel}
        </button>
      ) : null}
    </div>
  );
}
